import React, { useState, useContext } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Button,
  Typography,
  Container,
  Stack,
  Menu,
  MenuItem,
  useScrollTrigger,
  Slide,
  useTheme,
  useMediaQuery,
  Fade,
  ListItemIcon,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import LanguageIcon from '@mui/icons-material/Language';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import StarIcon from '@mui/icons-material/Star';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';
import { LangContext } from '../index';
import { Language } from '../../i18n';
import { useTranslation } from '../../i18n';
import tacticaLogo from '../../assets/images/tacticaLogov3.png';

const CONTACT_EMAIL = 'contact@tacticafoot.com';

interface NavbarProps {
  onContactClick: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onContactClick }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { lang, setLang } = useContext(LangContext);
  const { t } = useTranslation(lang);
  const trigger = useScrollTrigger();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLangClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLangClose = (newLang?: Language) => {
    if (newLang) {
      setLang(newLang);
    }
    setAnchorEl(null);
  };

  const handleContactClick = () => {
    window.location.href = `mailto:${CONTACT_EMAIL}`;
  };

  const menuItems = [
    { text: t('landing.nav.discover'), icon: <RocketLaunchIcon fontSize="small" />, onClick: () => navigate('/signup') },
    { text: t('landing.nav.pricing'), icon: <PriceChangeIcon fontSize="small" />, onClick: () => {
      navigate('/?section=pricing');
      setTimeout(() => {
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      setMobileOpen(false);
    }},
    { text: 'Témoignages', icon: <StarIcon fontSize="small" />, onClick: () => navigate('/temoignages') },
    { text: 'FAQ', icon: <HelpOutlineIcon fontSize="small" />, onClick: () => navigate('/faq') },
    { text: t('landing.nav.contact'), icon: <EmailIcon fontSize="small" />, onClick: () => navigate('/contact') },
    { 
      text: 'Support',
      icon: <WhatsAppIcon fontSize="small" />, 
      onClick: () => window.open('https://wa.me/33652682350', '_blank'),
      style: { 
        color: '#25D366',
        fontSize: '0.875rem',
        '&:hover': {
          color: '#128C7E',
          backgroundColor: 'rgba(255, 255, 255, 0.1)'
        },
        '& .MuiSvgIcon-root': {
          fontSize: '1.1rem',
          marginLeft: '4px'
        }
      }
    },
    { text: 'Me connecter', icon: <LoginIcon fontSize="small" />, onClick: () => window.location.href = 'https://app.tacticafoot.com/connexion' }
  ];

  const languages: { code: Language; label: string }[] = [
    { code: 'fr', label: 'FR' },
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
  ];

  const drawer = (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        bgcolor: 'rgba(0, 0, 0, 1)',
        color: 'white',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(5px)'
      }}
    >
      <IconButton
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: 'white'
        }}
        onClick={handleDrawerToggle}
      >
        <CloseIcon />
      </IconButton>

      <Stack 
        spacing={3} 
        alignItems="center"
      >
        {menuItems.map((item, index) => (
          <Fade
            key={index}
            in={mobileOpen}
            timeout={{ enter: 500 }}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            <div>
              <Button
                onClick={item.onClick}
                sx={{
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '1.2rem',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                aria-label={item.text}
                startIcon={item.icon}
              >
                {item.text}
              </Button>
            </div>
          </Fade>
        ))}
        <Fade
          in={mobileOpen}
          timeout={{ enter: 500 }}
          style={{ transitionDelay: `${menuItems.length * 100}ms` }}
        >
          <div>
            <Button
              sx={{
                color: 'white',
                textTransform: 'none',
                fontSize: '1.2rem',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
              onClick={handleLangClick}
              startIcon={<LanguageIcon />}
            >
              {lang.toUpperCase()}
            </Button>
          </div>
        </Fade>
      </Stack>
    </Box>
  );

  return (
    <>
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'none',
            boxShadow: 'none'
          }}
        >
          <Container maxWidth="xl">
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box
                component="img"
                src={tacticaLogo}
                alt="Tactica Logo"
                sx={{
                  height: '64px',
                  width: '64px',
                  objectFit: 'contain',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/')}
              />
              
              {isMobile ? (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Stack direction="row" spacing={2} alignItems="center">
                  {menuItems.map((item, index) => (
                    <Button
                      key={index}
                      onClick={item.onClick}
                      sx={{
                        color: 'white',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        ...(item.style || {}),
                      }}
                      aria-label={item.text}
                      startIcon={item.icon}
                    >
                      {item.text}
                    </Button>
                  ))}
                  <Button
                    sx={{
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                    onClick={handleLangClick}
                    startIcon={<LanguageIcon />}
                  >
                    {lang.toUpperCase()}
                  </Button>
                </Stack>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </Slide>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleLangClose()}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            mt: 1
          }
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLangClose(language.code)}
            selected={lang === language.code}
            sx={{
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)'
              },
              '&.Mui-selected': {
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }
            }}
          >
            <ListItemIcon>
              <LanguageIcon sx={{ color: 'white' }} />
            </ListItemIcon>
            {language.label}
          </MenuItem>
        ))}
      </Menu>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: '100%',
            bgcolor: 'black'
          }
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};
